'use client'

import { Calendar, Users, Coffee, Loader2, PartyPopper, Gamepad2, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import confetti from 'canvas-confetti'

const guideOptions = [
  { 
    id: 'create-event', 
    icon: PartyPopper, 
    text: " CREATE YOUR EVENT NOW",
    bgColor: 'bg-gradient-to-r from-green-400 to-emerald-400',
    hoverColor: 'hover:from-green-300 hover:to-emerald-300',
    action: 'Start by creating your first event',
    redirect: '/signup',
    isMain: true
  },
  { 
    id: 'explore-games', 
    icon: Gamepad2, 
    text: "Explore games & activities (Bingo, Pair, Deck, Cluster)",
    bgColor: 'bg-pink-400',
    hoverColor: 'hover:bg-pink-300',
    action: 'See all interactive formats for engagement',
    redirect: '/features#games'
  },
  { 
    id: 'organizing', 
    icon: Calendar, 
    text: "I'm organizing an event and want to learn what SocialLoop offers",
    bgColor: 'bg-blue-400',
    hoverColor: 'hover:bg-blue-300',
    action: 'Learn about features for event hosts and guests',
    redirect: '/features'
  },
  { 
    id: 'demo-first', 
    icon: Users, 
    text: "I want to see a live-style demo before committing",
    bgColor: 'bg-orange-400',
    hoverColor: 'hover:bg-orange-300',
    action: 'Try our interactive demo experience',
    redirect: '/onboarding-demo'
  },
  { 
    id: 'pricing', 
    icon: Coffee, 
    text: "I want to see pricing and plans",
    bgColor: 'bg-purple-400',
    hoverColor: 'hover:bg-purple-300',
    action: 'View our pricing options and features',
    redirect: '/pricing'
  }
]

export const EventSurveyCard = () => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleOptionSelect = (optionId: string) => {
    const selectedGuide = guideOptions.find(option => option.id === optionId)
    if (!selectedGuide) return
    
    // Mark that user selected an option
    sessionStorage.setItem('socialloop_option_selected', 'true')
    
    setSelectedOption(optionId)
    setIsLoading(true)
    
    setTimeout(() => {
      setIsLoading(false)
      setIsSubmitted(true)
      
      confetti({
        particleCount: 60,
        spread: 50,
        origin: { y: 0.6 },
        colors: ['#34d399', '#fbbf24', '#f472b6', '#60a5fa']
      })
      
      setTimeout(() => {
        window.location.href = selectedGuide.redirect
      }, 2000)
    }, 1000)
  }

  return (
    <motion.div
      layout
      initial={{ scale: 0.8, rotate: -5, y: 50 }}
      animate={{ scale: 1, rotate: 2, y: 0 }}
      whileHover={{ scale: 1.05, rotate: -1 }}
      className={cn(
        'w-full max-w-md mx-auto overflow-hidden border-4 border-black py-6 px-5 shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] bg-gradient-to-br from-yellow-300 to-orange-300 transform transition-all duration-500 relative'
      )}>
      
      {/* Decorative elements */}
      <div className="absolute -top-2 -left-2 w-6 h-6 bg-pink-400 border-2 border-black transform rotate-45 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"></div>
      <div className="absolute -top-1 -right-3 w-4 h-4 bg-blue-400 border-2 border-black rounded-full shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"></div>
      <div className="absolute -bottom-2 -left-3 w-5 h-3 bg-green-400 border-2 border-black transform -rotate-12 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"></div>
      
      <motion.div
        initial={{ y: -20, opacity: 0, scale: 0.8 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, type: "spring", bounce: 0.6 }}
        className="text-center mb-6">
        
        <motion.div 
          className="inline-block bg-red-400 border-4 border-black px-4 py-2 transform -rotate-3 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] mb-4"
          animate={{ rotate: [-3, 1, -3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <h3 className="text-xl font-black text-black flex items-center gap-2">
             WELCOME TO SOCIALLOOP
          </h3>
        </motion.div>
        
        <motion.div 
          className="bg-cyan-300 border-3 border-black px-3 py-2 transform rotate-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] inline-block"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: "spring", bounce: 0.8 }}
        >
          <p className="text-sm font-bold text-black">
            What would you like to do today?
          </p>
        </motion.div>
      </motion.div>

      <AnimatePresence mode="wait">
        {!isSubmitted ? (
          <motion.div
            key="options"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="space-y-4">
            
            {/* Main CTA Button */}
            {guideOptions.filter(option => option.isMain).map((option, index) => {
              const IconComponent = option.icon
              return (
                <motion.button
                  key={option.id}
                  initial={{ scale: 0.8, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 2,
                    boxShadow: '10px 10px 0px 0px rgba(0,0,0,1)'
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ 
                    delay: 0.2,
                    type: "spring",
                    bounce: 0.6
                  }}
                  onClick={() => handleOptionSelect(option.id)}
                  disabled={isLoading}
                  className={cn(
                    'w-full p-6 border-4 border-black text-center font-black text-lg text-black transform transition-all duration-300 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] relative overflow-hidden',
                    option.bgColor,
                    option.hoverColor,
                    selectedOption === option.id && 'rotate-3 scale-105',
                    isLoading && 'opacity-50 cursor-not-allowed'
                  )}>
                  
                  {/* Sparkle decorations */}
                  <div className="absolute top-2 left-2 w-2 h-2 bg-yellow-300 border border-black rounded-full"></div>
                  <div className="absolute top-3 right-4 w-1.5 h-1.5 bg-pink-300 border border-black rounded-full"></div>
                  <div className="absolute bottom-2 left-4 w-1 h-1 bg-blue-300 border border-black rounded-full"></div>
                  
                  <div className="flex flex-col items-center gap-3 relative z-10">
                    <motion.div
                      animate={selectedOption === option.id ? { rotate: 360, scale: 1.2 } : {}}
                      transition={{ duration: 0.8, ease: "easeInOut" }}
                      className="bg-black border-3 border-black p-3 rounded-lg"
                    >
                      <IconComponent 
                        size={32} 
                        className="text-white" 
                        strokeWidth={2.5}
                      />
                    </motion.div>
                    <span className="leading-tight">
                      {option.text}
                    </span>
                  </div>
                  
                  {isLoading && selectedOption === option.id && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute inset-0 bg-black/10 flex items-center justify-center"
                    >
                      <div className="bg-white border-3 border-black p-3 rounded shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                        <Loader2 className="animate-spin h-6 w-6 text-black" />
                      </div>
                    </motion.div>
                  )}
                </motion.button>
              )
            })}

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-0.5 bg-black transform rotate-1"></div>
              <div className="bg-yellow-300 border-2 border-black px-3 py-1 text-xs font-black text-black transform -rotate-1">
                OR
              </div>
              <div className="flex-1 h-0.5 bg-black transform -rotate-1"></div>
            </div>

            {/* Other Options */}
            <div className="grid grid-cols-1 gap-3">
              {guideOptions.filter(option => !option.isMain).map((option, index) => {
                const IconComponent = option.icon
                return (
                  <motion.button
                    key={option.id}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.6 }}
                    onClick={() => handleOptionSelect(option.id)}
                    disabled={isLoading}
                    className={cn(
                      'w-full p-3 border-3 border-black text-left font-bold text-sm text-black transform hover:scale-102 transition-all duration-200 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px]',
                      option.bgColor,
                      option.hoverColor,
                      selectedOption === option.id && 'rotate-2 scale-105',
                      isLoading && 'opacity-50 cursor-not-allowed'
                    )}>
                    <div className="flex items-start gap-3">
                      <IconComponent 
                        size={18} 
                        className="mt-0.5 flex-shrink-0" 
                        strokeWidth={2.5}
                      />
                      <span className="leading-tight">
                        {option.text}
                      </span>
                    </div>
                    {isLoading && selectedOption === option.id && (
                      <div className="flex items-center justify-center mt-2">
                        <Loader2 className="animate-spin h-4 w-4" />
                      </div>
                    )}
                  </motion.button>
                )
              })}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            variants={successContainer}
            initial="hidden"
            animate="show"
            className="flex flex-col items-center justify-center gap-4 py-8 text-center">
            
            <motion.div
              variants={successItem}
              className="flex h-16 w-16 items-center justify-center border-4 border-black bg-green-400 transform rotate-12 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
              <PartyPopper strokeWidth={3} size={24} className="stroke-black" />
            </motion.div>
            
            <motion.div variants={successItem} className="space-y-2">
              <div className="text-xl font-black text-black transform -rotate-1">
                PERFECT! 🎉
              </div>
              <div className="text-sm font-bold text-black/80">
                Taking you there now...
              </div>
              <div className="text-xs font-bold text-black/60 transform rotate-1">
                Get ready for something awesome!
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const successContainer = {
  hidden: { opacity: 0, scale: 0.8 },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.1
    }
  }
}

const successItem = {
  hidden: { y: 20, opacity: 0 },
  show: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.3 }
  }
}